import { Box, Divider, Button, MenuItem, Select, Typography, useTheme, BadgeProps, SxProps } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, getWeek, setDate, setDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { convertSqlTimeToDate, getFormattedHour } from '@kheops/utils';
import { useGetCompanyDeliveryHoursInfoQuery } from '../../queries/__generated__/getCompanyDeliveryHoursInfo.generated';
import { isDeliveryDay, numberOfWeeksFromNow } from '../utils/date.utils';
import { getStateLayerColor, StateLayer } from '../../theme/color.utils';

export interface DeliveryDatePickerProps {
  companyId: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
}

interface WeekOption {
  date: Date;
  label: string;
}

interface InlineBadgeProps {
  color: BadgeProps['color'];
  sx?: SxProps;
}

function InlineBadge({ color, sx }: InlineBadgeProps): React.JSX.Element {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '100%',
        backgroundColor: `${color}.main`,
        ...sx,
      }}
    />
  );
}

export default function DeliveryDatePicker({ companyId, value, onChange }: DeliveryDatePickerProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'order']);
  const theme = useTheme();
  const { data } = useGetCompanyDeliveryHoursInfoQuery({
    variables: {
      companyId,
    },
  });
  const [weekIndex, setWeekIndex] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<Date | null | undefined>(value);

  const hasDeliveryHours = useMemo((): boolean => {
    return !!data?.company_by_pk?.delivery_hours?.length;
  }, [data]);

  const weeks = useMemo((): WeekOption[] => {
    const results: WeekOption[] = [];
    const firstDay = new Date();
    let firstOptionLabel = `${t('common:week')} ${getWeek(firstDay, { weekStartsOn: 1 })}`;

    if (firstDay.getDay() !== 1) {
      firstOptionLabel += `-${getWeek(firstDay, { weekStartsOn: 1 }) + 1}`;
    }

    results.push({ date: firstDay, label: firstOptionLabel });

    for (let i = 0; i < 51; i++) {
      const monday = setDay(results[i].date, 1, { weekStartsOn: 1 });
      const nextMonday = setDate(monday, monday.getDate() + 7);

      results.push({
        date: nextMonday,
        label: `${t('common:week')} ${getWeek(nextMonday)}`,
      });
    }

    return results;
  }, []);

  const isSelectedDeliveryDay = useMemo((): boolean => {
    if (!selectedDate) {
      return false;
    }

    return isDeliveryDay(selectedDate.getDay(), data?.company_by_pk?.delivery_hours);
  }, [data, selectedDate]);

  const handleSelectChange = (newDate: Date): void => {
    if (onChange) {
      onChange(newDate);
      setSelectedDate(newDate);
    }
  };

  const days = useMemo((): Date[] => {
    const results: Date[] = [weeks[weekIndex].date];

    for (let i = 0; i < 6; i++) {
      const nextDay = new Date(results[results.length - 1]);

      nextDay.setDate(nextDay.getDate() + 1);
      results.push(nextDay);
    }

    return results;
  }, [weeks, weekIndex]);

  useEffect((): void => {
    if (weeks?.length && value) {
      const today = new Date();
      const initialDeliveryDate = new Date(value);

      today.setHours(0, 0, 0);
      initialDeliveryDate.setHours(0, 0, 0);

      setWeekIndex(
        numberOfWeeksFromNow(initialDeliveryDate),
      );
    }
  }, [weeks]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select
            value={weekIndex}
            onChange={(event) => setWeekIndex(event.target.value as number)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayOutlinedIcon />
                {weeks[selected].label}
              </Box>
            )}
            sx={{
              height: 48,
              width: 232,
              borderRadius: 3,
            }}
            MenuProps={{
              sx: {
                height: 372,
                '& .MuiTypography-root': {
                  lineHeight: 'normal',
                },
              },
            }}
          >
            {weeks.map(({ date, label }, index) => {
              const options = [(
                <MenuItem key={date.getTime()} value={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'start',
                    }}
                  >
                    <Typography variant="bodyLarge">
                      {label}
                    </Typography>
                    <Typography variant="bodyMedium" color="secondary">
                      {`${format(date, 'd MMM', { locale: fr })} - ${format(setDate(date, date.getDate() + 6), 'd MMM', { locale: fr })}`}
                    </Typography>
                  </Box>

                </MenuItem>
              )];

              if (label === `${t('common:week')} 1`) {
                const yearDivider = (
                  <MenuItem
                    sx={{
                      maxHeight: 36,
                      mt: 2,
                      py: 0,
                      '&.Mui-disabled': { opacity: 1 },
                    }}
                    disabled
                    value=""
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                      }}
                    >
                      <Divider flexItem />
                      <Typography variant="bodySmall" color="secondary" sx={{ pl: 2, pt: 1.5, pb: 1 }}>
                        {new Date().getFullYear() + 1}
                      </Typography>
                    </Box>
                  </MenuItem>
                );

                options.unshift(yearDivider);
              }

              return options.flat();
            })}
          </Select>
          <Box>
            <Button
              variant="text"
              disabled={weekIndex === 0}
              onClick={() => setWeekIndex(weekIndex - 1)}
            >
              <ArrowBackIcon />
            </Button>
            <Button
              variant="text"
              disabled={weekIndex === weeks.length - 1}
              onClick={() => setWeekIndex(weekIndex + 1)}
            >
              <ArrowForwardIcon />
            </Button>
          </Box>
        </Box>
        {hasDeliveryHours && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
            <InlineBadge color="success" />
            <Typography variant="bodySmall">{t('common:open')}</Typography>
            <Typography variant="bodySmall">/</Typography>
            <InlineBadge color="error" />
            <Typography variant="bodySmall">{t('common:closed')}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {days.map((day) => (
          <Box
            className={day.toDateString() === selectedDate?.toDateString() ? 'selected' : ''}
            key={day.toString()}
            sx={{
              width: 75,
              height: 48,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 0.5,
              px: 2,
              borderRadius: 2,
              boxShadow: `inset 0 0 0 1px ${theme.palette.outline.main}`,
              cursor: 'pointer',
              color: 'onSurface.main',
              whiteSpace: 'nowrap',
              '& .MuiTypography-root': {
                lineHeight: 'normal',
              },
              '&.selected, &.selected:hover': {
                boxShadow: `inset 0 0 0 2px ${theme.palette.onSurface.main}`,
                backgroundColor: getStateLayerColor(StateLayer.Layer12, theme.palette.surface.main, theme.palette.onSurface.main),
              },
              '&:hover': {
                backgroundColor: getStateLayerColor(StateLayer.Layer8, theme.palette.surface.main, theme.palette.onSurface.main),
              },
              position: 'relative',
            }}
            onClick={() => handleSelectChange(day)}
          >
            <Typography variant="labelLarge" sx={{ textTransform: 'capitalize' }}>
              {format(day, 'EEE', { locale: fr })}
            </Typography>
            <Typography variant="bodySmall" sx={{ color: 'onSurfaceVariant.main' }}>
              {format(day, 'd MMM', { locale: fr })}
            </Typography>
            {hasDeliveryHours && (
              <InlineBadge
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
                color={isDeliveryDay(day.getDay(), data?.company_by_pk?.delivery_hours) ? 'success' : 'error'}
              />
            )}
          </Box>
        ))}
      </Box>
      {hasDeliveryHours && selectedDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InlineBadge color={isSelectedDeliveryDay ? 'success' : 'error'} />
          <Typography variant="bodySmall">
            {
              isSelectedDeliveryDay
                ? t(
                  'order:open_from_to',
                  {
                    openingHours: getFormattedHour(convertSqlTimeToDate(data!.company_by_pk!.delivery_hours[0].opening_hour)),
                    closingHours: getFormattedHour(convertSqlTimeToDate(data!.company_by_pk!.delivery_hours[0].closing_hour)),
                  },
                )
                : t('common:closed')
            }
          </Typography>
        </Box>
      )}
      <Box>
        <Typography
          variant="bodySmall"
          sx={{
            color: 'onSurfaceVariant.main',
            mb: 1
          }}>
          {t('settings:additional_info')}
        </Typography>
        <Typography variant="bodySmall">
          {data?.company_by_pk?.delivery_description || '-'}
        </Typography>
      </Box>
    </Box>
  );
}
